import React, { useState, useEffect } from "react";
import { RiDownloadLine } from "react-icons/ri";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import ExcelJS from "exceljs";

import css from "./MenuComponent.module.css";
import MenuListComponent from "../MenuListComponent/MenuListComponent";
import { SliderCalendar } from "../../../ui";
import MenuModal from "../menuSectionModals/MenuModal/MenuModal";
import { CloneMenuForMenuComponent } from "../CloneMenuForMenuComponent/CloneMenuForMenuComponent";
import { menuMenuActions } from "../../../store/slices/menu-slices";
import { appSettingsActions } from "../../../store/slices/settings-slices";
import { Button } from "antd";
import { MdSync } from "react-icons/md";
import { toast } from "react-toastify";
import { menuMenuService } from "../../../services/menu-services/menuMenu.service";
import { CycleDayMenuComponent } from "../CycleDayMenuComponent/CycleDayMenuComponent";

const MenuComponent = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [isSyncing, setIsSyncing] = useState(false);

  const selectedDate = useSelector((state) => state.availableDishes.dataOfDay);
  const menuData = useSelector((state) => state.menuMenu.menuMenuPacksArray);
  const trigger = useSelector((state) => state.menuMenu.trigger);
  const currentLanguage = i18n.language;
  const menuFormat = useSelector((state) => state.appSettings.menuFormat);
  const isPeriodBased = menuFormat === "period_based";

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}.${month}.${year}`;
  };

  const handleClickXls = async () => {
    if (!menuData || menuData.length === 0) {
      alert(t("No menu data available for the selected date"));
      return;
    }

    const workbook = new ExcelJS.Workbook();
    workbook.creator = "GWT ERP";
    workbook.created = new Date();

    const worksheet = workbook.addWorksheet("menu");
    worksheet.columns = [{ width: 30 }, { width: 15 }];

    const titleRow = worksheet.addRow([`Menu on: ${formatDate(selectedDate)}`]);
    titleRow.font = { bold: true, size: 14 };
    worksheet.mergeCells("A1:B1");
    worksheet.getCell("A1").alignment = { horizontal: "center" };
    titleRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "E2EFDA" },
      };
    });

    const headerRow = worksheet.addRow(["Diet / Dish", "Dish weight (g)"]);
    headerRow.font = { bold: true, color: { argb: "FFFFFF" } };
    headerRow.getCell(1).fill = headerRow.getCell(2).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "4472C4" },
    };
    headerRow.alignment = { horizontal: "center" };

    menuData.forEach((pack) => {
      const packRow = worksheet.addRow([
        pack.name[currentLanguage] || pack.name["en"],
        "",
      ]);
      packRow.font = { bold: true };
      packRow.getCell(1).fill = packRow.getCell(2).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "E2EFDA" },
      };

      pack.menuDishes.forEach((menuDish) => {
        const dishRow = worksheet.addRow([
          menuDish.dish.name[currentLanguage] || menuDish.dish.name["en"],
          menuDish.dish.weight,
        ]);
        dishRow.getCell(1).fill = dishRow.getCell(2).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFE5CC" },
        };
      });
    });

    worksheet.eachRow((row) => {
      row.eachCell((cell) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `menu_${formatDate(selectedDate)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleSyncForFutureDays = async () => {
    try {
      setIsSyncing(true);
      const res = await menuMenuService.syncForFutureDays(selectedDate);
      dispatch(menuMenuActions.changeTrigger());
      toast.success(res.message);
    } catch (error) {
      console.error("Error syncing for future days:", error);
      toast.error("Error syncing for future days");
    } finally {
      setIsSyncing(false);
    }
  };

  useEffect(() => {
    dispatch(appSettingsActions.getAppSettings());
  }, [dispatch]);

  useEffect(() => {
    if (selectedDate && !isPeriodBased) {
      dispatch(menuMenuActions.getAllMenuPacks({ currentData: selectedDate }));
    }
  }, [dispatch, selectedDate, trigger, isPeriodBased]);

  if (isPeriodBased) {
    return (
      <section className={css.wrap}>
        <div className={css.blockBody} style={{ width: "100%" }}>
          <CycleDayMenuComponent />
        </div>
      </section>
    );
  }

  return (
    <section className={css.wrap}>
      <div className={css.blockCalendar}>
        <SliderCalendar />
      </div>

      <div className={css.blockBody}>
        <div className={css.blockSearch}>
          <CloneMenuForMenuComponent />
          <Button
            type="primary"
            onClick={handleSyncForFutureDays}
            icon={<MdSync />}
            loading={isSyncing}
            disabled={isSyncing}
          >
            Sync For Future Days
          </Button>
        </div>

        <div className={css.blockInfo}>
          <MenuListComponent setIsOpen={setIsOpen} />
        </div>

        <div className={css.blockImport}>
          <button onClick={handleClickXls}>
            xls
            <RiDownloadLine />
          </button>
        </div>
      </div>

      {isOpen && <MenuModal setIsOpen={setIsOpen} />}
    </section>
  );
};

export { MenuComponent };
